import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Search dietary preferences with keyword
export const searchDietaryPreferences = createAsyncThunk(
  'dietaryPreference/searchDietaryPreferences',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching dietary preferences with keyword:', keyword);
      const response = await api.dietaryPreferences.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Dietary preferences search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search dietary preferences:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching dietary preferences:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching dietary preferences');
    }
  }
);

// Fetch dietary preferences
export const fetchDietaryPreferences = createAsyncThunk(
  'dietaryPreference/fetchDietaryPreferences',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching dietary preferences with page:', page, 'and limit:', limit);
      const response = await api.dietaryPreferences.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Dietary preferences fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch dietary preferences:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching dietary preferences:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching dietary preferences');
    }
  }
);

// Create dietary preference
export const createDietaryPreference = createAsyncThunk(
  'dietaryPreference/createDietaryPreference',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating dietary preference with data:', formData);
      const response = await api.dietaryPreferences.create(formData);
      if (response.data.status) {
        log.info('Dietary preference created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create dietary preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating dietary preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create dietary preference');
    }
  }
);

// Update dietary preference
export const updateDietaryPreference = createAsyncThunk(
  'dietaryPreference/updateDietaryPreference',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating dietary preference with ID:', id, 'and data:', formData);
      const response = await api.dietaryPreferences.update(id, formData);
      if (response.data.status) {
        log.info('Dietary preference updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update dietary preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating dietary preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating dietary preference');
    }
  }
);

// Delete dietary preference
export const deleteDietaryPreference = createAsyncThunk(
  'dietaryPreference/deleteDietaryPreference',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting dietary preference with ID:', id);
      const response = await api.dietaryPreferences.delete(id);
      if (response.data.status) {
        log.info('Dietary preference deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete dietary preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting dietary preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting dietary preference');
    }
  }
);

// Bulk delete dietary preferences
export const bulkDeleteDietaryPreferences = createAsyncThunk(
  'dietaryPreference/bulkDeleteDietaryPreferences',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.dietaryPreferences.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting dietary preferences');
    }
  }
);

// Bulk activate/deactivate dietary preferences
export const bulkUpdateDietaryPreferenceStatus = createAsyncThunk(
  'dietaryPreference/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.dietaryPreferences.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating dietary preference status');
    }
  }
);

// Fetch a single dietary preference by ID
export const fetchDietaryPreferenceById = createAsyncThunk(
  'dietaryPreference/fetchDietaryPreferenceById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching dietary preference with ID:', id);
      const response = await api.dietaryPreferences.getById(id);
      if (response.data.status) {
        log.info('Dietary preference fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch dietary preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching dietary preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching dietary preference');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const dietaryPreferenceSlice = createSlice({
  name: 'dietaryPreference',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch dietary preferences
      .addCase(fetchDietaryPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDietaryPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.dietaryPreferences;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchDietaryPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search dietary preferences
      .addCase(searchDietaryPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchDietaryPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.dietaryPreferences || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchDietaryPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create dietary preference
      .addCase(createDietaryPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDietaryPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createDietaryPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update dietary preference
      .addCase(updateDietaryPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDietaryPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateDietaryPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete dietary preference
      .addCase(deleteDietaryPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDietaryPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteDietaryPreference.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete dietary preferences
      .addCase(bulkDeleteDietaryPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteDietaryPreferences.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteDietaryPreferences.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateDietaryPreferenceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateDietaryPreferenceStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateDietaryPreferenceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch a single dietary preference by ID
      .addCase(fetchDietaryPreferenceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDietaryPreferenceById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = [action.payload]; // Assuming we want to replace the current items with the fetched one
      })
      .addCase(fetchDietaryPreferenceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default dietaryPreferenceSlice.reducer; 